// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import type {
  ValueCreationGrouping,
  ValueCreationHolding,
} from '@shared/data-access/interfaces/common/aggregator';
import type { Tag } from '@shared/data-access/interfaces/redux/tag-manager-state';
import { isUndefined } from '@shared/utils';
import { groupBy } from '@shared/ui-kit';

// istanbul ignore next
const createValueCreationHolding = (
  name: string,
  weight: number,
  investment: number,
  returnValue = 0,
): ValueCreationHolding => {
  const initialValue = investment / (1 + returnValue);

  const valueCreated = investment - initialValue;

  return {
    name,
    weight,
    initialValue,
    finalValue: investment,
    valueCreated,
    return: returnValue,
  };
};

// istanbul ignore next
const createValueCreationAssetClass = (
  name: string,
  weight: number,
  investment: number,
  holdings: ValueCreationHolding[],
): ValueCreationHolding => {
  const initialValue = holdings.reduce(
    (acc, { initialValue }) => acc + initialValue,
    0,
  );

  const valueCreated = holdings.reduce(
    (acc, { valueCreated }) => acc + valueCreated,
    0,
  );

  const returnValue = valueCreated / initialValue || 0;

  return {
    name,
    weight,
    initialValue,
    finalValue: investment,
    valueCreated,
    return: returnValue,
  };
};

export const buildValueCreationHoldings = (
  holdings: AccountsHoldingsData[],
  grouping: ValueCreationGrouping,
  returnPeriod?:
    | 'm1_return'
    | 'm3_return'
    | 'm6_return'
    | 'ytd_return'
    | 'y1_return',
  tags: Tag[] = [],
): ValueCreationHolding[] => {
  if (isUndefined(returnPeriod)) {
    return [];
  }

  const filteredHoldings = holdings.filter(
    holding => holding.asset_class?.toLocaleLowerCase() !== 'other',
  );

  switch (grouping) {
    case 'assetClass': {
      const assetClasses = groupBy(filteredHoldings, 'asset_class');

      return Array.from(assetClasses.entries()).map(
        ([assetClass, holdings]): ValueCreationHolding => {
          const investment = holdings.reduce(
            (acc, holding) => acc + holding.investment,
            0,
          );

          const weight = holdings.reduce((acc, holding) => acc + holding.weight, 0);

          const creationHoldings = holdings.map(({ name, investment, details }) =>
            createValueCreationHolding(
              name,
              0,
              investment,
              details?.[returnPeriod],
            ),
          );

          return createValueCreationAssetClass(
            assetClass!,
            weight,
            investment,
            creationHoldings,
          );
        },
      );
    }

    case 'account': {
      const accounts = groupBy(filteredHoldings, 'institution_name');

      return Array.from(accounts.entries()).map(([institutionName, holdings]) => {
        const totalInvestment = filteredHoldings.reduce(
          (acc, holding) => acc + holding.investment,
          0,
        );

        const creationHoldings = holdings.map(({ name, investment, details }) =>
          createValueCreationHolding(name, 0, investment, details?.[returnPeriod]),
        );

        const investment = creationHoldings.reduce(
          (acc, { finalValue }) => acc + finalValue,
          0,
        );

        const weight = investment / totalInvestment || 0;

        return createValueCreationAssetClass(
          institutionName,
          weight,
          investment,
          creationHoldings,
        );
      });
    }

    case 'positions': {
      return filteredHoldings.map(holding => {
        const returnValue = holding.details?.[returnPeriod] ?? 0;

        const name = holding.name ?? holding.ticker ?? holding.isin;

        return createValueCreationHolding(
          name,
          holding.weight,
          holding.investment,
          returnValue,
        );
      });
    }

    case 'categories': {
      // const holdings = data.flatMap(account =>
      //   account.holdings.flatMap(assetClass =>
      //     assetClass.subRows.flatMap(assetType => assetType.subRows),
      //   ),
      // );

      // // istanbul ignore next
      // const totalInvestment = holdings.reduce(
      //   (acc, { investment }) => acc + (investment ?? 0),
      //   0,
      // );

      // // istanbul ignore next
      // return tags
      //   .filter(tag => {
      //     const holdingsByTicker = holdings.filter(holding =>
      //       tag.tickers.includes(holding.ticker ?? holding.isin ?? ''),
      //     );

      //     return tag.tickers.length > 0 && holdingsByTicker.length > 0;
      //   })
      //   .map(tag => {
      //     const holdingsByTicker = holdings
      //       .filter(holding =>
      //         tag.tickers.includes(holding.ticker ?? holding.isin ?? ''),
      //       )
      //       .map(({ name, investment, details }) =>
      //         createValueCreationHolding(
      //           name,
      //           0,
      //           investment,
      //           details?.[returnPeriod],
      //         ),
      //       );

      //     const investment = holdingsByTicker.reduce(
      //       (acc, { finalValue }) => acc + finalValue,
      //       0,
      //     );

      //     const weight = investment / totalInvestment || 0;

      //     return createValueCreationAssetClass(
      //       tag.name,
      //       weight,
      //       investment,
      //       holdingsByTicker,
      //     );
      //   });
      return [];
    }
  }
};
